import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ApiError } from "./client";
import { getArtifact, getChatTemplates, getThread, listArtifacts, listThreads, normalizeModels, type Artifact, type GenModel } from "./chat";
import { useTool } from "./hooks";
import { lastBrand, useBrandId } from "@/state/brand";

const noClientRetry = (n: number, e: unknown) => !(e instanceof ApiError && e.status < 500 && !e.isNetwork) && n < 1;

export const threadsKey = ["chat", "threads"] as const;
export const threadKey = (id: string | null | undefined, brandId: string | null = lastBrand()) => ["chat", "thread", id, brandId] as const;

export function useThreads(opts: { enabled?: boolean; brandId?: string | null } = {}) {
  const selected = useBrandId();
  const brandId = opts.brandId === undefined ? selected : opts.brandId;
  return useQuery({
    queryKey: [...threadsKey, brandId],
    queryFn: ({ signal }) => listThreads(signal, brandId),
    staleTime: 15_000,
    enabled: !!brandId && opts.enabled !== false,
    placeholderData: undefined,
    retry: noClientRetry,
  });
}

/**
 * Thread + messages. `poll` refetches every 2.5s (a run is active but this tab doesn't hold its stream);
 * `frozen` stops background refetches while this tab is streaming (the live store owns the tail).
 */
export function useThread(id: string | null | undefined, opts: { poll?: boolean; frozen?: boolean; brandId?: string | null } = {}) {
  const selected = useBrandId();
  const brandId = opts.brandId === undefined ? selected : opts.brandId;
  return useQuery({
    queryKey: threadKey(id, brandId),
    queryFn: ({ signal }) => getThread(id!, signal, brandId),
    enabled: !!id && !!brandId,
    placeholderData: undefined,
    staleTime: opts.frozen ? Infinity : 5_000,
    // Poll while a run is live on the server but this tab doesn't hold its stream (other replica / re-attach ended).
    refetchInterval: (query) => (opts.frozen ? false : opts.poll || (query.state.data as { thread?: { status?: string } | null } | undefined)?.thread?.status === "running" ? 2500 : false),
    retry: noClientRetry,
  });
}

export function useArtifact(id: string | null | undefined, brandId?: string | null) {
  return useQuery({
    queryKey: ["chat", "artifact", id, brandId],
    queryFn: ({ signal }) => getArtifact(id!, { brandId, signal }),
    enabled: !!id,
    placeholderData: undefined,
    staleTime: 60_000,
    retry: noClientRetry,
  });
}

/**
 * ‹ › navigation: other report documents of the same kind for the brand (reports.list), ordered by publish
 * time. Revisions of the current document are navigated with the "Revision N" chip.
 */
export function useArtifactSiblings(a: Artifact | null | undefined) {
  const brandId = a?.brandId ?? null;
  const q = useQuery({
    queryKey: ["chat", "artifact-siblings", brandId],
    queryFn: ({ signal }) => listArtifacts(brandId!, signal),
    enabled: !!brandId,
    staleTime: 60_000,
    retry: noClientRetry,
  });
  const list = useMemo(() => {
    if (!a) return [];
    const docs = (q.data ?? []).filter((x) => !a.kind || !x.kind || x.kind === a.kind);
    // Replace the current document's entry with the revision being viewed.
    const all = docs.filter((x) => (a.documentId ? x.documentId !== a.documentId : x.id !== a.id));
    all.push(a);
    all.sort((x, y) => (Date.parse(x.periodEnd ?? x.publishedAt ?? "") || 0) - (Date.parse(y.periodEnd ?? y.publishedAt ?? "") || 0));
    return all;
  }, [q.data, a]);
  const idx = a ? list.findIndex((x) => x.id === a.id) : -1;
  return { ...q, list, prev: idx > 0 ? list[idx - 1] : undefined, next: idx >= 0 && idx < list.length - 1 ? list[idx + 1] : undefined, index: idx };
}

export function useChatTemplates() {
  return useQuery({ queryKey: ["chat", "templates"], queryFn: ({ signal }) => getChatTemplates(signal), staleTime: 30 * 60_000, retry: noClientRetry });
}

/** Image + video generation models for the composer chips (`creatives.models {}`). */
export function useChatModels(brandId: string | null | undefined) {
  const q = useTool<unknown>("creatives.models", brandId ? { brandId } : null, { staleTime: 10 * 60_000 });
  const models = useMemo<GenModel[]>(() => normalizeModels(q.data), [q.data]);
  const image = useMemo(() => models.filter((m) => m.kind === "image"), [models]);
  const video = useMemo(() => models.filter((m) => m.kind === "video"), [models]);
  return { ...q, models, image, video };
}
